import { getDomainGA4Analytics } from "@mp-commons/config/GA4Accounts.js";
import { NielsenAudicomMeasurementTrackingIds, NielsenVideoMeasurementTrackingIds } from "@mp-commons/config/NielsenAccounts.js";
import { getSecondLevelDomain } from "@mp-commons/lib/utils/DomainNameUtils.js";
import { getCPProductFromHostname, getYMHostByCPProduct } from "@mp-commons/config/CPProducts.js";
import { CP_EmbedType } from "@mp-commons/modules/ads/adcall/CustomParams.js";
import { ExternallyPassedContentAdData } from "@mp-commons/modules/ads/adcall/composable/ContentSpecificAdCallFilter.js";
import { NielsenCollectorFactory } from "@mp-commons/modules/stats/collectors/NielsenCollector.js";
import { AttachableEventMapper, MapperConfigFlags } from "@mp-commons/modules/stats/mappers/CommonEventMapper.js";
import { GA4Mapper } from "../../stats/mappers/GA4Mapper.js";
import { MegarideCSStreamingTagMapper } from "../../stats/mappers/MegarideCSStreamingTagMapper.js";
import { MegarideNielsenMapper } from "../../stats/mappers/MegarideNielsenMapper.js";
import { MegarideYMStatsMapper } from "../../stats/mappers/MegarideYMStatsMapper.js";
import { GDPRPromise } from "@mp-commons/modules/ads/GDPR.js";
import { MegarideAudicomMapper } from "../../stats/mappers/MegarideAudicomMapper.js";
import { AudicomConfig } from "@mp-commons/modules/stats/collectors/AudicomPairingPing.js";
import { generateUID } from "@mp-commons/lib/utils/Utility.js";

export interface GenericStatsProviderFlags extends MapperConfigFlags {
    enable: boolean
}


export interface StatsConfig  {

    ga4: GenericStatsProviderFlags,
    nielsen: {enable: boolean},
    comscore: GenericStatsProviderFlags,
    ymstats: GenericStatsProviderFlags,
    playerName: string,
    audicom: { enable: boolean },
}


/**
 * Builds an array of event mappers for analytics and tracking based on the provided configuration.
 *
 * This function creates and returns a list of `AttachableEventMapper` instances for various analytics providers
 * (GA4, Nielsen, YMStats, Comscore) depending on the enabled flags in the `StatsConfig` object.
 *
 * @param win - The browser window object, used for accessing location and other window-specific properties.
 * @param embedType - The type of embed for the player, used for analytics context.
 * @param config - The configuration object specifying which analytics providers are enabled and their settings.
 * @param externalData - Optional externally passed content/ad data for analytics mappers that require it.
 * @returns An array of initialized `AttachableEventMapper` instances for the enabled analytics providers.
 */
export function StatsMapperBuilder(win: Window,
                                    embedType: CP_EmbedType,
                                    config: StatsConfig,
                                    externalData?: ExternallyPassedContentAdData
                                    ): AttachableEventMapper[] {
    const product = getCPProductFromHostname(win.location.hostname);
    const seclev = getSecondLevelDomain(win.location.hostname);
    
    
                            

    const mappers: AttachableEventMapper[] = [];

    if(config.ga4.enable) {
        const ga4config = getDomainGA4Analytics(seclev);
        mappers.push(new GA4Mapper(win,embedType,{
            ...ga4config,
            ...config.ga4
        }));
    }

    if(config.nielsen.enable && product in NielsenVideoMeasurementTrackingIds) {
        const trackingIdMapper = NielsenVideoMeasurementTrackingIds[product];
        const nielsenfactory = new NielsenCollectorFactory(win,trackingIdMapper,config.playerName);
        mappers.push(new MegarideNielsenMapper(nielsenfactory));
    }
    if(config.ymstats.enable) {
        const ymHostname = getYMHostByCPProduct(win.location.hostname,seclev,product);
        mappers.push(new MegarideYMStatsMapper(win,{
            ...config.ymstats,
            embedType: embedType,
            playerName: config.playerName,
            ymHostname: ymHostname,
            product: product
        }))
    }


    const audicomEnable = config.audicom.enable;
    const nielsenAudicomConfigurationAvailable = product in NielsenAudicomMeasurementTrackingIds;

    console.log("[AUDICOM] Audicom mapping enabled? ", audicomEnable, nielsenAudicomConfigurationAvailable);
    if (audicomEnable && nielsenAudicomConfigurationAvailable) {
        
        const audicomAppIdRetrieval: () => string = () => {
            const trackingIdMapper = NielsenAudicomMeasurementTrackingIds[product];
            return trackingIdMapper.default
        }
        const appID = audicomAppIdRetrieval();
        const GUID = generateUID();
        console.log("[AUDICOM] Retrieved Audicom App ID: ", appID);
        console.log("[AUDICOM] Generated GUID: ", GUID);

        const audicomConfig: AudicomConfig = {
            appID: appID,
            sessionID: GUID
        }

        const audicom = new MegarideAudicomMapper(win, {
            comscore: {
                publisherId: "18904044",
                debug: true
            },
            audicom: audicomConfig
        }, GDPRPromise(), externalData);

        mappers.push(audicom);

    } else if (config.comscore.enable) {
        const cscore = new MegarideCSStreamingTagMapper(win, {
            publisherId: "18904044",
            debug: true
        }, GDPRPromise(), externalData);
        mappers.push(cscore);
    }


    return mappers;
}
